/** @jsxImportSource @emotion/react */
import { FlagOutlined, MoreOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { getHighQualityImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { Dropdown } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useState } from 'react'
import Linkify from 'react-linkify'

import { Flex } from '../Layout'
import ReportModal from '../ReportModal'
import Text from '../ts-components/Text'

const getInitials = (name = '') => {
  const [first = '', last = ''] = name.split(' ')
  return [first.charAt(0), last.charAt(0)]
}

/**
 * ChatMessage component for displaying individual messages in a chat
 * @param {Object} props - Component props
 * @param {Object} props.message - The message object to display
 * @param {string} props.loggedInUserId - ID of the currently logged in user
 * @param {string} props.previousUserId - ID of the user who sent the previous message
 * @param {string} props.nextUserId - ID of the user who sent the next message
 * @returns {JSX.Element} ChatMessage component
 */
const ChatMessage = ({
  message,
  loggedInUserId,
  previousUserId,
  nextUserId,
}) => {
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)

  const isSelf = loggedInUserId === message.user_id
  const isSameUserAsBefore = previousUserId === message.user_id
  const isSameUserAsNext = nextUserId === message.user_id
  const isPlayer =
    typeof message?.player_id === 'string' && message.player_id.length > 0
  const [firstInitial, lastInitial] = getInitials(message?.user?.name)

  const imageThumbnailId =
    getHighQualityImageThumbnailId(message) || message?.image_url

  // Handle report submission
  const handleReport = useCallback(async () => {
    try {
      // TODO: Implement actual report API call
      console.log('Reporting message:', message.id)
      return Promise.resolve()
    } catch (err) {
      console.error('Failed to report message:', err)
      return Promise.reject(err)
    }
  }, [message.id])

  // Handle opening report modal
  const handleOpenReportModal = (e) => {
    // e.stopPropagation()
    setIsReportModalOpen(true)
  }

  return (
    <>
      <Flex
        noFlex
        flexDirection={isSelf ? 'row-reverse' : 'row'}
        css={css`
          margin-top: ${isSameUserAsBefore ? 3 : 10}px;
          position: relative;
        `}
      >
        <div>
          {!isSameUserAsBefore && !isSelf && (
            <Text
              variant='paragraphXSmall'
              weight='semibold'
              color={colors.NEUTRAL_50}
              style={css`
                margin-bottom: 4px;
                margin-left: 8px;
              `}
            >
              {message?.user?.name}
            </Text>
          )}
          <Flex alignItems='flex-end'>
            {/* {!isSelf && !isSameUserAsNext && (
              <Flex
                as={isPlayer ? Link : undefined}
                to={
                  isPlayer
                    ? `${ROUTES.PLAYER_ROOT}/${message.player_id}`
                    : undefined
                }
                css={
                  isPlayer &&
                  css`
                    &:hover .initials-thumbnail {
                      border-color: ${colors.PRIMARY};
                      color: ${colors.PRIMARY};
                    }
                  `
                }
              >
                {message?.user?.image?.filename ? (
                  <Picture
                    size='kxxsmall'
                    imageId={getImageThumbnailId(message?.user)}
                  />
                ) : (
                  <InitialsThumbnail size={30}>
                    {firstInitial}
                    {lastInitial}
                  </InitialsThumbnail>
                )}
              </Flex>
            )} */}
            <div
              css={css`
                /* min-width: 108px; */
                max-width: 250px;
                padding: 7px 8px 4px 8px;
                border-radius: 12px;
                margin-left: 8px;
                ${isSameUserAsBefore &&
                (isSelf
                  ? 'border-top-right-radius: 4px;'
                  : 'border-top-left-radius: 4px;')}

                ${isSameUserAsNext &&
                (isSelf
                  ? 'border-bottom-right-radius: 4px;'
                  : 'border-bottom-left-radius: 4px;')}

                background-color: ${isSelf
                  ? isCanlan
                    ? colors.INFO_500
                    : colors.INFO_500
                  : colors.SECONDARY_100};
                position: relative;

                /* margin-left: ${!isSelf && !isSameUserAsNext ? 6 : 36}px; */
                &:hover .message-actions {
                  opacity: 1;
                }
              `}
            >
              {/* Message actions dropdown */}
              {!isSelf && (
                <div
                  className='message-actions'
                  css={css`
                    position: absolute;
                    top: -8px;
                    ${isSelf ? 'left: -8px;' : 'right: -8px;'}
                    opacity: 0;
                    transition: opacity 0.2s;
                    z-index: 1;
                  `}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: '1',
                          icon: <FlagOutlined style={{ color: '#E53935' }} />,
                          label: 'Report',
                          onClick: handleOpenReportModal,
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <MoreOutlined
                      css={css`
                        color: ${colors.SOLITUDE};
                        font-size: 18px;
                        padding: 2px;
                        border-radius: 50%;
                        background-color: rgba(0, 0, 0, 0.5);
                        cursor: pointer;
                        &:hover {
                          color: ${colors.WHITE};
                        }
                      `}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Dropdown>
                </div>
              )}

              <div
                css={css`
                  color: ${!isSelf || isCanlan
                    ? colors.NEUTRAL_0
                    : colors.NEUTRAL_0};
                  text-align: ${isSelf ? 'right' : 'left'};
                  word-break: break-word;
                  a {
                    text-decoration: underline;
                    color: ${!isSelf || isCanlan
                      ? colors.NEUTRAL_0
                      : colors.SECONDARY_300};
                  }
                `}
              >
                {imageThumbnailId ? (
                  <img
                    src={imageThumbnailId}
                    alt='chat-image'
                    css={css`
                      max-width: 100%;
                      max-height: 200px;
                    `}
                  />
                ) : (
                  <Linkify
                    properties={{
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    }}
                  >
                    <Text
                      variant='paragraphSmall'
                      weight='regular'
                      style={css`
                        letter-spacing: 0px;
                        line-height: 100% !important;
                      `}
                    >
                      {message?.message}
                    </Text>
                  </Linkify>
                )}
              </div>
              <div
                css={css`
                  font-size: 10px;
                  color: ${isSelf
                    ? isCanlan
                      ? '#38d1ff'
                      : colors.NEUTRAL_50
                    : colors.ATTENDANCE_GRAY};
                  text-align: ${isSelf ? 'left' : 'right'};
                  margin-top: 4px;
                `}
              >
                {dayjs.utc(message?.created_at).local().format('h:mm a')}
              </div>
            </div>
          </Flex>
        </div>
      </Flex>

      {/* Report Modal */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        onReport={handleReport}
        entityType='message'
        entityId={message?.id}
        title='Report Message'
      />
    </>
  )
}

export default ChatMessage
